/**
 * CR309: V2 Help Callout
 * 
 * Inline help text + links to V2 rollout controls in Layout Studio.
 */

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ExternalLink, 
  Settings2, 
  Eye, 
  Bug,
  LayoutGrid,
} from "lucide-react";

interface V2HelpCalloutProps {
  className?: string;
}

export function V2HelpCallout({ className }: V2HelpCalloutProps) {
  const handleOpenLayoutStudio = () => {
    // Navigate to Layout Studio
    window.open("/admin/layout-studio?tab=deal-cards", "_blank");
  };
  
  const handleOpenPreview = () => {
    // Open current page with V2 enabled
    const url = new URL(window.location.href);
    url.searchParams.set("dealCardV2", "1");
    window.open(url.toString(), "_blank");
  };
  
  const handleOpenCompare = () => {
    // Open compare view
    const url = new URL(window.location.href);
    url.searchParams.set("dealCardV2", "1");
    url.searchParams.set("compare", "1");
    window.open(url.toString(), "_blank");
  };
  
  const handleOpenDebug = () => {
    // Open debug view
    const url = new URL(window.location.href);
    url.searchParams.set("dealCardV2", "1");
    url.searchParams.set("dealCardV2debug", "1");
    window.open(url.toString(), "_blank");
  };
  
  return (
    <div className={cn(
      "bg-muted/50 border border-border/50 rounded-lg p-3 space-y-2",
      className
    )}>
      <div className="flex items-center gap-2 text-sm">
        <LayoutGrid className="h-4 w-4 text-primary" />
        <span className="font-medium">V2 Rollout Controls</span>
        <Badge variant="outline" className="text-[9px]">
          Layout Studio
        </Badge>
      </div>
      
      <p className="text-xs text-muted-foreground">
        Rollout controls (enable/disable, percent ramp, allowlist) live in{" "}
        <button 
          onClick={handleOpenLayoutStudio}
          className="text-primary hover:underline font-medium inline-flex items-center gap-0.5"
        >
          Layout Studio → V2 Rollout Status Card
          <ExternalLink className="h-3 w-3" />
        </button>
      </p>
      
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={handleOpenPreview}
        >
          <Eye className="h-3 w-3" />
          V2 Preview
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={handleOpenCompare}
        >
          <Settings2 className="h-3 w-3" />
          Compare V1/V2
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={handleOpenDebug}
        >
          <Bug className="h-3 w-3" />
          Debug View
        </Button>
      </div>
    </div>
  );
}

export default V2HelpCallout;
